<template>
    <Views :loading="loading" :error="error">
        <ApplicationFilter :applications="applications" @filter="setFilter" class="mb-4" />

        <div class="legend mb-3">
            <div v-for="s in statuses" class="item">
                <div class="count" :class="s.color">{{ s.count }}</div>
                <div class="label">{{ s.name }}</div>
            </div>
        </div>

        <div v-if="!items.length" class="empty-state grey--text">No applications found</div>

        <v-card v-for="group in applicationGroups" :key="group.id" outlined class="application-group mb-3">
            <v-btn text block class="group-heading" @click="toggleGroup(group.id)">
                <v-icon small class="mr-2">{{ expandedGroups[group.id] ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
                <v-icon small class="mr-2">mdi-folder</v-icon>
                <span class="group-name">{{ group.name }}</span>
                <v-chip small label class="ml-3">{{ group.items.length }}</v-chip>
                <v-spacer />
            </v-btn>

            <v-expand-transition>
                <div v-show="expandedGroups[group.id]">
                    <ApplicationTable v-if="group.items.length" :items="group.items" :headers="headers" />
                    <div v-else class="group-empty grey--text">
                        {{ group.emptyMessage }}
                    </div>
                </div>
            </v-expand-transition>
        </v-card>
    </Views>
</template>

<script>
import ApplicationFilter from '../components/ApplicationFilter.vue';
import ApplicationTable from '../components/ApplicationTable.vue';
import Views from '@/views/Views.vue';

const statuses = {
    critical: { name: 'SLO violation', color: 'red lighten-1' },
    warning: { name: 'Warning', color: 'orange lighten-1' },
    info: { name: 'Errors in logs', color: 'blue lighten-1' },
    unknown: { name: 'Integration required', color: 'purple lighten-1' },
    ok: { name: 'OK', color: 'green lighten-1' },
};

export default {
    components: { Views, ApplicationFilter, ApplicationTable },

    data() {
        return {
            applications: [],
            filter: new Set(),
            expandedGroups: {
                'knowledge-hub': true,
                'social-intelligence-platform': true,
            },
            loading: false,
            error: '',
        };
    },

    mounted() {
        this.get();
        this.$events.watch(this, this.get, 'refresh');
    },

    computed: {
        headers() {
            let headers = [
                { value: 'application', text: 'Application', sortable: false },
                { value: 'cluster', text: 'Cluster', sortable: false },
                { value: 'type', text: 'Type', sortable: false },
                { value: 'errors', text: 'Errors', sortable: false, align: 'end' },
                { value: 'latency', text: 'Latency', sortable: false, align: 'end' },
                { value: 'upstreams', text: 'Upstreams', sortable: false, align: 'end' },
                { value: 'instances', text: 'Instances', sortable: false, align: 'end' },
                { value: 'restarts', text: 'Restarts', sortable: false, align: 'end' },
                { value: 'cpu', text: 'CPU', sortable: false, align: 'end' },
                { value: 'memory', text: 'Mem', sortable: false, align: 'end' },
                { value: 'disk_io_load', text: 'I/O load', sortable: false, align: 'end' },
                { value: 'disk_usage', text: 'Disk', sortable: false, align: 'end' },
                { value: 'network', text: 'Net', sortable: false, align: 'end' },
                { value: 'dns', text: 'DNS', sortable: false, align: 'end' },
                { value: 'logs', text: 'Logs', sortable: false, align: 'center' },
            ];
            if (!this.$api.context.multicluster) {
                return headers.filter((h) => h.value !== 'cluster');
            }
            return headers;
        },
        multicluster() {
            return this.$api.context.multicluster;
        },
        categories() {
            return Array.from(new Set((this.applications || []).map((a) => a.category)).values());
        },
        statuses() {
            return Object.keys(statuses).map((s) => {
                return {
                    ...statuses[s],
                    count: this.items.filter((i) => i.status === s).length,
                };
            });
        },
        items() {
            if (!this.applications) {
                return [];
            }

            const applications = this.applications.filter((a) => this.filter.has(a.id)).map((a) => ({ ...a }));
            const names = {};
            applications.forEach((a) => {
                const id = this.$utils.appId(a.id);
                a.name = id.name;
                a.ns = id.ns;
                if (names[id.name]) {
                    names[id.name]++;
                } else {
                    names[id.name] = 1;
                }
            });
            return applications.map((a) => {
                return {
                    ...a,
                    ns: names[a.name] > 1 ? a.ns : '',
                    color: statuses[a.status].color,
                };
            });
        },
        applicationGroups() {
            const knownKnowledgeHubApps = new Set(['api-portal', 'user-portal', 'admin-portal', 'crawler-service']);
            const knownSocialIntelligencePlatformApps = new Set([
                'dashboard',
                'grafana',
                'prometheus',
                'mongodb-exporter',
                'kafka-exporter',
                'kafka',
                'consumer',
                'mrs-app',
                'scanner',
                'crawl-social-media',
                'camofox',
                'search-collector',
            ]);
            const composeContainerNamePattern = /^((media-risk-dashboard|dashboard-portal)[-_]dashboard[-_]\d+|grafana[-_](grafana|prometheus|mongodb-exporter|kafka-exporter)[-_]\d+|media-risk-consumer[-_]consumer[-_]\d+|news[-_]scanner[-_]\d+)$/i;
            const allItems = this.items;
            const knowledgeHub = allItems.filter((app) => knownKnowledgeHubApps.has(app.name.toLowerCase()));
            const socialIntelligencePlatform = allItems.filter((app) => {
                const name = app.name.toLowerCase();
                return knownSocialIntelligencePlatformApps.has(name) || composeContainerNamePattern.test(name);
            });
            const assignedIds = new Set([...knowledgeHub, ...socialIntelligencePlatform].map((app) => app.id));
            const other = allItems.filter((app) => !assignedIds.has(app.id));

            const groups = [
                {
                    id: 'knowledge-hub',
                    name: 'Knowledge Hub',
                    items: knowledgeHub,
                    emptyMessage: 'Không có service Knowledge Hub khớp với bộ lọc.',
                },
                {
                    id: 'social-intelligence-platform',
                    name: 'Social Intelligence Platform',
                    items: socialIntelligencePlatform,
                    emptyMessage: 'Chưa nhận dữ liệu service Social Intelligence Platform trên Coroot.',
                },
            ];

            if (other.length) {
                groups.push({
                    id: 'other',
                    name: 'Data service',
                    items: other,
                    emptyMessage: 'Không có service khớp với bộ lọc.',
                });
            }

            return groups;
        },
    },

    methods: {
        get() {
            this.loading = true;
            this.error = '';
            this.$api.getOverview('applications', '', (data, error) => {
                this.loading = false;
                if (error) {
                    this.error = error;
                    return;
                }
                this.applications = data.applications;
            });
        },
        setFilter(filter) {
            this.filter = filter;
        },
        toggleGroup(groupId) {
            this.$set(this.expandedGroups, groupId, !this.expandedGroups[groupId]);
        },
        link(id, report, query) {
            return { name: 'overview', params: { view: 'applications', id, report }, query: { ...query, ...this.$utils.contextQuery() } };
        },
    },
};
</script>

<style scoped>
.table:deep(table) {
    min-width: 500px;
}
.table:deep(tr:hover) {
    background-color: unset !important;
}
.table:deep(th) {
    white-space: nowrap;
}
.table:deep(th),
.table:deep(td) {
    padding: 4px 8px !important;
}
.table:deep(td:has(.application)) {
    padding-left: 0 !important;
}
.table .application {
    display: flex;
    gap: 4px;
}
.table .application .status {
    height: 20px;
    width: 4px;
}
.table .application .name {
    max-width: 30ch;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.table .application .cluster {
    max-width: 20ch;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.table .logs {
    display: block;
    position: relative;
    height: 100%;
    color: inherit;
}
.table .logs .value {
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;
    white-space: nowrap;
    display: flex;
    align-items: center;
    justify-content: center;
}
.table:deep(td:has(.logs)) {
    width: 120px;
    min-width: 120px;
    padding: 0 !important;
}

.type {
    opacity: 60%;
    white-space: nowrap;
}
.type.unknown {
    opacity: 100%;
    border-bottom: 2px solid #ab47bc !important;
    background-color: unset !important;
}
.type.ok {
    opacity: 100%;
}
.type.critical,
.type.warning {
    opacity: 100%;
    border-bottom: 2px solid red !important;
    background-color: unset !important;
}

.value {
    color: inherit;
    opacity: 60%;
    white-space: nowrap;
}
.value.critical,
.value.warning {
    opacity: 100%;
    border-bottom: 2px solid red !important;
    background-color: unset !important;
}
.legend {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
    font-weight: 500;
    font-size: 14px;
}
.legend .item {
    display: flex;
    gap: 4px;
}
.legend .count {
    padding: 0 4px;
    border-radius: 2px;
    height: 18px;
    line-height: 18px;
    color: rgba(255, 255, 255, 0.8);
}
.legend .label {
    opacity: 60%;
}
.application-group {
    overflow: hidden;
}
.group-heading {
    justify-content: flex-start;
    min-height: 48px;
    text-transform: none;
    letter-spacing: normal;
}
.group-name {
    font-size: 16px;
    font-weight: 500;
}
.group-empty,
.empty-state {
    padding: 18px 20px;
}
</style>
